import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { FileOutput, AlertCircle, RefreshCw, Download, CheckCircle, Copy, Check } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
import { useApp } from '../context/AppContext';
import { ReviewStatus, NonCompliantItem } from '../types';
import { DISCLAIMER } from '../constants';
import { cleanDuplicatedNumbering, extractClauseNumber } from '../utils/clauses';

// 复制到剪贴板的工具函数
const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    const success = document.execCommand('copy');
    document.body.removeChild(textArea);
    return success;
  }
};

const ReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const { 
    currentProject, 
    report, 
    reviewResults,
    clauses,
    loadProject, 
    loadReport,
    generateReport,
  } = useApp();
  
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [downloadReady, setDownloadReady] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // 处理复制
  const handleCopy = async (id: string, text: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // Load project and report
  useEffect(() => {
    if (projectId && projectId !== 'new') {
      const loadData = async () => {
        await loadProject(projectId);
        try {
          await loadReport(projectId);
          setDownloadReady(true);
        } catch {
          // Report may not exist yet, that's ok
        }
        setLoading(false);
      };
      loadData();
    }
  }, [projectId]);

  const handleGenerateReport = async () => {
    if (!projectId) return;
    
    setGenerating(true);
    setDownloadReady(false);
    
    try {
      await generateReport(projectId);
      
      // Poll for report completion
      const pollReport = async (attempts = 0): Promise<void> => {
        if (attempts > 30) {
          setGenerating(false);
          return;
        }
        
        try {
          await loadReport(projectId);
          setGenerating(false);
          setDownloadReady(true);
          
          // Auto download after generation
          await handleDownloadReport();
          
          // Reload project to update status
          await loadProject(projectId);
        } catch {
          await new Promise(resolve => setTimeout(resolve, 1000));
          return pollReport(attempts + 1);
        }
      };
      
      await pollReport();
    } catch (e) {
      console.error('Failed to generate report:', e);
      setGenerating(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!projectId) return;
    
    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
      const downloadUrl = `${baseUrl}/projects/${projectId}/report/download`;
      
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Download failed');
      }
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = '审查报告.docx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
        if (match) {
          filename = decodeURIComponent(match[1]);
        }
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  // Calculate stats from report or review results
  const stats = report ? {
    total: report.total_clauses,
    compliant: report.compliant_count,
    nonCompliant: report.non_compliant_count,
    uncertain: report.uncertain_count,
    ignored: report.ignored_count,
    exempted: report.exempted_count,
  } : {
    total: clauses.length,
    compliant: Array.from(reviewResults.values()).filter(r => r.status === ReviewStatus.COMPLIANT).length,
    nonCompliant: Array.from(reviewResults.values()).filter(r => r.status === ReviewStatus.NON_COMPLIANT).length,
    uncertain: Array.from(reviewResults.values()).filter(r => r.status === ReviewStatus.UNCERTAIN).length,
    ignored: Array.from(reviewResults.values()).filter(r => r.status === ReviewStatus.IGNORED).length,
    exempted: Array.from(reviewResults.values()).filter(r => r.status === ReviewStatus.EXEMPTED).length,
  };

  const chartData = [
    { name: '合规', value: stats.compliant, color: '#10b981' },
    { name: '不合规', value: stats.nonCompliant, color: '#f43f5e' },
    { name: '不确定', value: stats.uncertain, color: '#f59e0b' },
    { name: '其他', value: stats.ignored + stats.exempted, color: '#94a3b8' },
  ].filter(d => d.value > 0);

  // Get issues from report or review results
  interface IssueItem extends NonCompliantItem {
    status?: ReviewStatus;
  }
  
  const issues: IssueItem[] = report?.non_compliant_items.map(item => ({
    ...item,
    status: ReviewStatus.NON_COMPLIANT,
  })) || 
    Array.from(reviewResults.entries())
      .filter(([_, r]) => r.status === ReviewStatus.NON_COMPLIANT || r.status === ReviewStatus.UNCERTAIN)
      .map(([clauseId, r]) => {
        const clause = clauses.find(c => c.id === clauseId);
        return {
          clause_id: clauseId,
          clause_no: clause?.clause_no || `#${clause?.sequence}`,
          original_text: clause?.content || '',
          issues: r.issues || [],
          evidence: r.evidence || [],
          suggested_cn: r.suggested_cn,
          suggested_en: r.suggested_en,
          user_edit: null,
          confidence: r.confidence,
          status: r.status,
        };
      });

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-7xl mx-auto space-y-3 animate-fade-in h-[calc(100vh-64px)] overflow-hidden flex flex-col">
      {/* Header Row */}
      <div className="flex justify-between items-center flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-slate-900">合规性总结报告</h1>
          <p className="text-sm text-slate-500">{currentProject?.name}</p>
        </div>
        <div className="flex gap-2 items-center">
          {downloadReady && report && (
            <span className="text-xs text-emerald-600 flex items-center gap-1">
              <CheckCircle size={14} />
              v{report.version}
            </span>
          )}
          {downloadReady && (
            <button 
              onClick={handleDownloadReport}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-emerald-500 text-emerald-600 rounded-lg hover:bg-emerald-50 text-sm font-medium"
            >
              <Download size={14} />
              下载
            </button>
          )}
          <button 
            onClick={handleGenerateReport}
            disabled={generating}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 text-sm font-medium disabled:opacity-50"
          >
            {generating ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                生成中...
              </>
            ) : (
              <>
                <RefreshCw size={14} />
                {report ? '重新生成' : '生成报告'}
              </>
            )}
          </button>
          <button 
            onClick={() => navigate(`/project/${projectId}/export`)}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 text-sm font-medium"
          >
            <FileOutput size={14} />
            导出
          </button>
        </div>
      </div>

      {/* Disclaimer - Compact */}
      <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex-shrink-0">
        <p className="text-xs text-amber-700">{DISCLAIMER.zh}</p>
      </div>

      {/* Stats Row - Horizontal compact */}
      <div className="grid grid-cols-5 gap-2 flex-shrink-0">
        <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 flex items-center justify-between">
          <span className="text-slate-500 text-xs">总条款</span>
          <span className="text-xl font-bold text-slate-900">{stats.total}</span>
        </div>
        <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 flex items-center justify-between">
          <span className="text-emerald-600 text-xs">合规</span>
          <span className="text-xl font-bold text-emerald-600">{stats.compliant}</span>
        </div>
        <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 border-l-4 border-l-rose-500 flex items-center justify-between">
          <span className="text-rose-600 text-xs">不合规</span>
          <span className="text-xl font-bold text-rose-600">{stats.nonCompliant}</span>
        </div>
        <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 flex items-center justify-between">
          <span className="text-amber-600 text-xs">不确定</span>
          <span className="text-xl font-bold text-amber-600">{stats.uncertain}</span>
        </div>
        <div className="bg-white px-4 py-2 rounded-lg border border-slate-200 flex items-center justify-between">
          <span className="text-slate-500 text-xs">合规率</span>
          <span className="text-xl font-bold text-slate-900">
            {stats.total > 0 ? ((stats.compliant / stats.total) * 100).toFixed(0) : 0}%
          </span>
        </div>
      </div>

      {/* Main Content: Chart + Issues - Fill remaining space */}
      <div className="grid grid-cols-4 gap-3 flex-1 min-h-0">
        {/* Chart - Compact */}
        <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col">
          <h3 className="font-semibold text-slate-800 text-sm mb-2">风险分布</h3>
          <div className="flex-1 min-h-0">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                暂无数据
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-2 justify-center pt-2 border-t border-slate-100">
            {chartData.map((entry, index) => (
              <div key={index} className="flex items-center gap-1 text-xs">
                <div className="w-2 h-2 rounded" style={{ backgroundColor: entry.color }}></div>
                <span>{entry.name}: {entry.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Issues List - Takes more space */}
        <div className="bg-white rounded-lg border border-slate-200 col-span-3 flex flex-col min-h-0">
          <div className="px-4 py-2 border-b border-slate-100 bg-slate-50 flex justify-between items-center flex-shrink-0">
            <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
              <AlertCircle className="text-rose-500" size={16} />
              需关注的问题
            </h3>
            <span className="text-xs font-mono text-slate-500">{issues.length} 项</span>
          </div>
          
          <div className="divide-y divide-slate-100 overflow-y-auto flex-1">
            {issues.length > 0 ? issues.map((item: IssueItem, idx: number) => {
              const cleanedClauseNo = extractClauseNumber(item.clause_no) || cleanDuplicatedNumbering(item.clause_no || '') || `#${idx + 1}`;
              const cleanedOriginalText = cleanDuplicatedNumbering(item.original_text);
              
              // 检测原文是否主要是中文（中文字符占比超过30%认为是中文）
              const chineseCharCount = (cleanedOriginalText.match(/[\u4e00-\u9fa5]/g) || []).length;
              const isChinese = chineseCharCount / cleanedOriginalText.length > 0.3;
              
              // 根据原文语言选择对应的建议修改
              const suggestedText = isChinese ? item.suggested_cn : item.suggested_en;
              const cleanedSuggested = suggestedText ? cleanDuplicatedNumbering(suggestedText) : null;
              
              return (
                <div key={idx} className="px-4 py-3 hover:bg-slate-50 transition-colors group">
                  <div className="flex justify-between items-center gap-3 mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-xs font-bold text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded shrink-0 max-w-24 truncate">
                        {cleanedClauseNo}
                      </span>
                      {item.issues && item.issues.length > 0 && (
                        <span className="text-xs text-rose-600 truncate">{item.issues[0]}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <StatusBadge status={item.status || ReviewStatus.NON_COMPLIANT} size="sm" />
                      <button 
                        onClick={() => navigate(`/project/${projectId}/review?clauseId=${encodeURIComponent(item.clause_id)}`)}
                        className="text-xs text-slate-400 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        详情
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="relative">
                      <div className="flex items-center justify-between mb-0.5">
                        <h4 className="text-xs text-slate-400">原文</h4>
                        <button
                          onClick={() => handleCopy(`orig-${idx}`, cleanedOriginalText)}
                          className="p-0.5 text-slate-300 hover:text-slate-600 rounded transition-colors"
                          title="复制"
                        >
                          {copiedId === `orig-${idx}` ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
                        </button>
                      </div>
                      <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded border border-slate-100 line-clamp-2">
                        {cleanedOriginalText}
                      </p>
                    </div>
                    {cleanedSuggested && (
                      <div className="relative">
                        <div className="flex items-center justify-between mb-0.5">
                          <h4 className="text-xs text-emerald-600">建议修改</h4>
                          <button
                            onClick={() => handleCopy(`sug-${idx}`, cleanedSuggested)}
                            className="p-0.5 text-emerald-300 hover:text-emerald-600 rounded transition-colors"
                            title="复制"
                          >
                            {copiedId === `sug-${idx}` ? <Check size={10} className="text-emerald-500" /> : <Copy size={10} />}
                          </button>
                        </div>
                        <p className="text-xs text-slate-800 bg-emerald-50 p-2 rounded border border-emerald-100 line-clamp-2">
                          {cleanedSuggested}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            }) : (
              <div className="p-8 text-center text-slate-400">
                <AlertCircle size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">没有发现需要关注的问题</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPage;
